import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetFixedAssetDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  fixedAssetId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the fixed asset detail page.
 *
 * Fetches the fixed asset in a single HTTP round trip on client-side
 * navigation. Throws `notFound()` (not a bare Response) when the asset is
 * missing — thrown Responses can't be serialized by the server RPC layer
 * (Seroval crash → 500) and aren't rejected properly on the client, which
 * left the loader hanging on a blank screen with the progress bar stuck.
 * `notFound()` renders the route's `notFoundComponent` with a clean 404.
 */
export const getFixedAssetDetailPageData = createServerFn({
  method: "GET",
})
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetFixedAssetDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssetDetail.pageData", async () => {
      const { getFixedAssetForUser } = await import("./fixed-assets.server");
      const fixedAsset = await getFixedAssetForUser(
        context.user.id,
        data.businessId,
        data.fixedAssetId,
      );

      if (!fixedAsset) {
        throw notFound();
      }

      return { fixedAsset } as const;
    });
  });
